import React from 'react';
import { AlertCircleIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { MCP_LEAD_TYPES, MCP_SUCCESS_MESSAGE, ZOHO_WEBFORM } from '../../data/mcp';
import { BOOK_DEMO_URL, PRIVACY_URL } from '../../data/dgpt';
import { useMcpWaitlist, type WaitlistField } from '../../hooks/useMcpWaitlist';
import { useMcpWaitlistPopup } from '../../contexts/McpWaitlistContext';
import { cn } from '../../src/lib/utils';
const inputClass = 'mt-1.5 block w-full rounded-lg border bg-panel px-3.5 py-3 text-[15px] text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime';
type FieldDef = {
  field: WaitlistField;
  name: string;
  label: string;
  required?: boolean;
  type?: string;
  autoComplete?: string;
  maxLength: number;
};
const TEXT_FIELDS: FieldDef[] = [{
  field: 'firstName',
  name: 'First Name',
  label: 'First name',
  required: true,
  autoComplete: 'given-name',
  maxLength: 40
}, {
  field: 'lastName',
  name: 'Last Name',
  label: 'Last name',
  required: true,
  autoComplete: 'family-name',
  maxLength: 80
}, {
  field: 'email',
  name: 'Email',
  label: 'Email',
  required: true,
  type: 'email',
  autoComplete: 'email',
  maxLength: 100
}, {
  field: 'phone',
  name: 'Phone',
  label: 'Phone',
  type: 'tel',
  autoComplete: 'tel',
  maxLength: 30
}, {
  field: 'company',
  name: 'Company',
  label: 'Company',
  autoComplete: 'organization',
  maxLength: 200
}];
type McpWaitlistFormProps = {
  /** Unique per instance so labels and the iframe target don't collide. */
  idPrefix: string;
  /** Render without the card frame (used inside the popup). */
  bare?: boolean;
};
export function McpWaitlistForm({
  idPrefix,
  bare = false
}: McpWaitlistFormProps) {
  const {
    preview
  } = useMcpWaitlistPopup();
  const {
    values,
    errors,
    status,
    setField,
    onSubmit,
    onTargetLoad,
    reset,
    fieldId,
    formRef,
    isPreview
  } = useMcpWaitlist(idPrefix, preview);
  const target = `${idPrefix}-zoho-target`;
  const submitting = status === 'submitting';
  const frame = bare ? '' : 'rounded-2xl border border-hairline bg-panel p-6 shadow-sm sm:p-8';
  return <>
      {/* Zoho's response loads here, keeping visitors on the page. */}
      <iframe name={target} title="Waitlist submission" className="hidden" aria-hidden="true" tabIndex={-1} onLoad={onTargetLoad} data-id="element-767" data-mp="36klbb95ynts" />

      {status === 'success' ? <div role="status" className={cn(bare ? '' : 'rounded-2xl border border-[#B7E4C7] bg-[#F1FAF4] p-6 sm:p-8')} data-id="element-768" data-mp="3ph6pxcxgant1">
          <CheckCircle2Icon className="h-7 w-7 text-[#1E7A46]" aria-hidden="true" data-id="element-769" data-mp="qxgm2xvlktd6" />
          <p className="mt-4 text-lg font-semibold text-text-primary" data-id="element-770" data-mp="m0gdogb9zeiw">{MCP_SUCCESS_MESSAGE}</p>
          {isPreview ? <p className="mt-3 text-xs font-semibold uppercase tracking-[0.08em] text-[#9A5B13]" data-id="element-771" data-mp="gyhc9bz3irol">Design preview · nothing was sent</p> : null}
          <button type="button" onClick={reset} className="mt-5 inline-flex min-h-[44px] items-center text-sm font-semibold text-lime hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-772" data-mp="31qb3kvbrkkt6">
            Add another person
          </button>
        </div> : <form ref={formRef} name={ZOHO_WEBFORM.formName} action={ZOHO_WEBFORM.action} method="POST" acceptCharset="UTF-8" target={target} noValidate onSubmit={onSubmit} aria-busy={submitting} className={frame} data-id="element-773" data-mp="1h91v363dhf8o">
          {Object.entries(ZOHO_WEBFORM.hidden).map(([name, value]) => <input key={name} type="hidden" name={name} id={name === 'ldeskuid' || name === 'LDTuvid' ? `${idPrefix}-${name}` : undefined} defaultValue={value} data-id="element-774" data-mp="20v6i6uva6g6b" />)}

          <div className="grid gap-5 sm:grid-cols-2" data-id="element-775" data-mp="29xfthmydidkr">
            {TEXT_FIELDS.map(def => {
          const id = fieldId(def.field);
          const error = errors[def.field];
          return <div key={def.field} className={def.field === 'email' || def.field === 'company' ? 'sm:col-span-2' : undefined} data-id="element-776" data-mp="fi3k8jevhkkl">
                  <label htmlFor={id} className="text-sm font-medium text-text-primary" data-id="element-777" data-mp="3gcjgiv7o3hwh">
                    {def.label}{' '}
                    {def.required ? <span className="text-[#B42318]" aria-hidden="true" data-id="element-778" data-mp="3qck1ell6q62s">*</span> : <span className="font-normal text-text-tertiary" data-id="element-779" data-mp="3le8sqe6mm1m5">(optional)</span>}
                  </label>
                  <input id={id} name={def.name} type={def.type ?? 'text'} autoComplete={def.autoComplete} maxLength={def.maxLength} required={def.required} value={values[def.field]} onChange={event => setField(def.field, event.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} className={cn(inputClass, error ? 'border-[#D92D20]' : 'border-hairline')} data-id="element-780" data-mp="2xligs4yq3ibv" />
                  {error ? <p id={`${id}-error`} className="mt-1.5 flex items-center gap-1.5 text-[13px] text-[#B42318]" data-id="element-781" data-mp="u7htojlqiltn">
                      <AlertCircleIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-782" data-mp="fjn256jgqni5" />
                      {error}
                    </p> : null}
                </div>;
        })}

            <div className="sm:col-span-2" data-id="element-783" data-mp="184oc2d2jqiic">
              <label htmlFor={fieldId('leadType')} className="text-sm font-medium text-text-primary" data-id="element-784" data-mp="1d4j1ec6ob01a">
                Which best describes you? <span className="text-[#B42318]" aria-hidden="true" data-id="element-785" data-mp="z5jcr4j5ul54">*</span>
              </label>
              <select id={fieldId('leadType')} name="LEADCF5" required value={values.leadType || '-None-'} onChange={event => setField('leadType', event.target.value === '-None-' ? '' : event.target.value)} aria-invalid={Boolean(errors.leadType)} aria-describedby={errors.leadType ? `${fieldId('leadType')}-error` : undefined} className={cn(inputClass, errors.leadType ? 'border-[#D92D20]' : 'border-hairline', !values.leadType && 'text-text-tertiary')} data-id="element-786" data-mp="135tkomqijlmm">
                <option value="-None-" disabled data-id="element-787" data-mp="1fpqgeocazczk">
                  Choose one
                </option>
                {MCP_LEAD_TYPES.map(option => <option key={option.value} value={option.value} className="text-text-primary" data-id="element-788" data-mp="16osmiyvkwlup">
                    {option.label}
                  </option>)}
              </select>
              {errors.leadType ? <p id={`${fieldId('leadType')}-error`} className="mt-1.5 flex items-center gap-1.5 text-[13px] text-[#B42318]" data-id="element-789" data-mp="wspbf3ir1cvm">
                  <AlertCircleIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-790" data-mp="2w1w2f4yaoas8" />
                  {errors.leadType}
                </p> : null}
            </div>
          </div>

          {status === 'error' ? <div role="alert" className="mt-5 flex gap-2.5 rounded-xl border border-[#F5C2C0] bg-[#FFF6F5] px-4 py-3 text-sm text-text-primary" data-id="element-791" data-mp="33mznrn03gr98">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#B42318]" aria-hidden="true" data-id="element-792" data-mp="38f9j5ijzajiw" />
              <p data-id="element-793" data-mp="3dfowwb869t1e">
                We couldn’t confirm your signup. Please try again, or{' '}
                <a href={BOOK_DEMO_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2" data-id="element-794" data-mp="2gp4s511pqy8p">
                  talk to our team
                </a>
                .
              </p>
            </div> : null}

          <button type="submit" disabled={submitting} className="mt-6 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-lime px-6 text-[15px] font-semibold text-white transition-colors hover:bg-[#2B1E7A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-80" data-id="element-795" data-mp="2uo4h69t51f5e">
            {submitting ? <>
                <Loader2Icon className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" data-id="element-796" data-mp="14qfzy3cg3voy" />
                Joining…
              </> : 'Join the waitlist'}
          </button>
          <p className="mt-3 text-xs text-text-tertiary" data-id="element-797" data-mp="1enkmw5e47v6t">
            We’ll use these details to contact you about MCP access. See our{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" data-id="element-798" data-mp="19s59tfnxm3bg">
              Privacy Policy
            </a>
            .
          </p>
        </form>}
    </>;
}